export type BodyTemplateTextSegment = {
  id: string;
  kind: "text";
  value: string;
};

export type BodyTemplatePlaceholderSegment = {
  id: string;
  kind: "placeholder";
  source: string;
  useJsonFilter: boolean;
};

export type BodyTemplateSegment =
  | BodyTemplateTextSegment
  | BodyTemplatePlaceholderSegment;

const SYSTEM_SOURCES: Array<{
  id: string;
  templateVar: string;
  defaultJson: boolean;
}> = [
  { id: "@formId", templateVar: "form_id", defaultJson: false },
  { id: "@formName", templateVar: "form_name", defaultJson: true },
  { id: "@submissionId", templateVar: "submission_id", defaultJson: false },
  { id: "@submissionUuid", templateVar: "submission_uuid", defaultJson: true },
  { id: "@submittedAt", templateVar: "submitted_at", defaultJson: true },
  { id: "@deliveryId", templateVar: "delivery_id", defaultJson: true },
  { id: "@answers", templateVar: "answers_json", defaultJson: true },
];

const TEMPLATE_VAR_TO_SOURCE = SYSTEM_SOURCES.reduce<Record<string, string>>(
  (acc, item) => {
    acc[item.templateVar] = item.id;
    acc[item.templateVar.replace(/_([a-z])/g, (_, c) => c.toUpperCase())] =
      item.id;
    return acc;
  },
  {
    formId: "@formId",
    formName: "@formName",
    submissionId: "@submissionId",
    submissionUuid: "@submissionUuid",
    submittedAt: "@submittedAt",
    deliveryId: "@deliveryId",
    answers: "@answers",
  },
);

let segmentIdSeq = 0;

function nextSegmentId(): string {
  segmentIdSeq += 1;
  return `bts-${segmentIdSeq}`;
}

const PLACEHOLDER_RE = /\{\{([^}|]+?)(?:\|(\w+))?\}\}/g;

export function displayBodyTemplate(raw?: string): string {
  return raw ?? "";
}

export type TemplatePlaceholderRef = {
  id: string;
  start: number;
  end: number;
  source: string;
  useJsonFilter: boolean;
  raw: string;
};

export function listTemplatePlaceholderRefs(
  template: string,
): TemplatePlaceholderRef[] {
  const text = displayBodyTemplate(template);
  const refs: TemplatePlaceholderRef[] = [];
  const re = new RegExp(PLACEHOLDER_RE.source, "g");
  let match: RegExpExecArray | null;
  let index = 0;

  while ((match = re.exec(text)) !== null) {
    const varName = match[1].trim();
    refs.push({
      id: `ph-${index++}`,
      start: match.index,
      end: match.index + match[0].length,
      raw: match[0],
      source: TEMPLATE_VAR_TO_SOURCE[varName] ?? varName,
      useJsonFilter: match[2] === "json",
    });
  }

  return refs;
}

export function patchTemplatePlaceholder(
  template: string,
  ref: TemplatePlaceholderRef,
  patch: { source?: string; useJsonFilter?: boolean; remove?: boolean },
): string {
  const source = patch.source ?? ref.source;
  const useJsonFilter = patch.useJsonFilter ?? ref.useJsonFilter;
  const replacement = patch.remove
    ? ""
    : placeholderText(source, useJsonFilter);
  return template.slice(0, ref.start) + replacement + template.slice(ref.end);
}

export function defaultBodyTemplate(): string {
  return "{\n  \n}";
}

export function sourceToTemplateVar(source: string): string {
  const system = SYSTEM_SOURCES.find((item) => item.id === source);
  if (system) return system.templateVar;
  return source;
}

export function parseFieldPropertySource(
  source: string,
): { field: string; property: "label" | "value" } | null {
  const match = source.trim().match(/^([^.]+)\.(label|value)$/);
  if (!match) return null;
  return { field: match[1]!, property: match[2] as "label" | "value" };
}

export function defaultJsonForSource(source: string): boolean {
  const system = SYSTEM_SOURCES.find((item) => item.id === source);
  if (system) return system.defaultJson;
  const labeled = parseFieldPropertySource(source);
  if (labeled?.property === "value") return false;
  return true;
}

export function placeholderText(
  source: string,
  useJsonFilter: boolean,
): string {
  if (!source.trim()) return "{{…}}";
  const varName = sourceToTemplateVar(source.trim());
  return useJsonFilter ? `{{${varName}|json}}` : `{{${varName}}}`;
}

export function segmentsSignature(segments: BodyTemplateSegment[]): string {
  return JSON.stringify(
    segments.map((segment) =>
      segment.kind === "text"
        ? ["t", segment.value]
        : ["p", segment.source, segment.useJsonFilter],
    ),
  );
}

export function mergeAdjacentTextSegments(
  segments: BodyTemplateSegment[],
): BodyTemplateSegment[] {
  const merged: BodyTemplateSegment[] = [];

  for (const segment of segments) {
    const prev = merged[merged.length - 1];
    if (segment.kind === "text" && prev?.kind === "text") {
      merged[merged.length - 1] = {
        ...prev,
        value: prev.value + segment.value,
      };
    } else {
      merged.push({ ...segment });
    }
  }

  return merged;
}

export function parseBodyTemplateSegments(
  template: string,
): BodyTemplateSegment[] {
  const text = displayBodyTemplate(template);
  if (!text) {
    return [
      { id: nextSegmentId(), kind: "text", value: defaultBodyTemplate() },
    ];
  }

  const segments: BodyTemplateSegment[] = [];
  let lastIndex = 0;
  const re = new RegExp(PLACEHOLDER_RE.source, "g");
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        id: nextSegmentId(),
        kind: "text",
        value: text.slice(lastIndex, match.index),
      });
    }

    const varName = match[1].trim();
    const filter = match[2];
    const source = TEMPLATE_VAR_TO_SOURCE[varName] ?? varName;

    segments.push({
      id: nextSegmentId(),
      kind: "placeholder",
      source,
      useJsonFilter: filter === "json",
    });

    lastIndex = re.lastIndex;
  }

  if (lastIndex < text.length) {
    segments.push({
      id: nextSegmentId(),
      kind: "text",
      value: text.slice(lastIndex),
    });
  }

  if (segments.length === 0) {
    segments.push({ id: nextSegmentId(), kind: "text", value: text });
  }

  return segments;
}

export function buildBodyTemplateFromSegments(
  segments: BodyTemplateSegment[],
): string {
  return segments
    .map((segment) => {
      if (segment.kind === "text") return segment.value;
      return placeholderText(segment.source, segment.useJsonFilter);
    })
    .join("");
}

export function insertFormFieldIntoTemplate(
  template: string,
  field: { name: string; type: string },
): string {
  const text = displayBodyTemplate(template).trim();
  const useJson = field.type !== "birthdate" && field.type !== "date";
  const ph = placeholderText(field.name, useJson);
  const entry = `"${field.name}": ${ph}`;

  if (
    !text ||
    text === "{}" ||
    text === defaultBodyTemplate() ||
    text === "{\n\n}"
  ) {
    return `{\n  ${entry}\n}`;
  }

  const trimmed = text.trimEnd();
  if (trimmed.endsWith("}")) {
    const inner = trimmed.slice(0, -1).trimEnd();
    const needsComma = !inner.endsWith("{") && inner.length > 0;
    return `${inner}${needsComma ? "," : ""}\n  ${entry}\n}`;
  }

  return `${trimmed},\n  ${entry}`;
}

const SYSTEM_API_KEYS: Record<string, string> = {
  "@formId": "formId",
  "@formName": "formName",
  "@submissionId": "submissionId",
  "@submissionUuid": "submissionUuid",
  "@submittedAt": "submittedAt",
  "@deliveryId": "deliveryId",
  "@answers": "answers",
};

export function insertSystemSourceIntoTemplate(
  template: string,
  sourceId: string,
): string {
  const apiKey = SYSTEM_API_KEYS[sourceId] ?? sourceId.replace(/^@/, "");
  const ph = placeholderText(sourceId, defaultJsonForSource(sourceId));
  const entry = `"${apiKey}": ${ph}`;
  const text = displayBodyTemplate(template).trim();

  if (
    !text ||
    text === "{}" ||
    text === defaultBodyTemplate() ||
    text === "{\n\n}"
  ) {
    return `{\n  ${entry}\n}`;
  }

  const trimmed = text.trimEnd();
  if (trimmed.endsWith("}")) {
    const inner = trimmed.slice(0, -1).trimEnd();
    const needsComma = !inner.endsWith("{") && inner.length > 0;
    return `${inner}${needsComma ? "," : ""}\n  ${entry}\n}`;
  }

  return `${trimmed},\n  ${entry}`;
}

export function listPlaceholderSources(
  segments: BodyTemplateSegment[],
): string[] {
  return segments
    .filter(
      (segment): segment is BodyTemplatePlaceholderSegment =>
        segment.kind === "placeholder",
    )
    .map((segment) => segment.source)
    .filter(Boolean);
}

function placeholderValidationStub(varName: string, filter?: string): string {
  const name = varName.trim();
  if (filter === "json") {
    if (/answers|json/i.test(name)) return "{}";
    return '"__placeholder__"';
  }
  if (/id$/i.test(name) || /Id$/.test(name) || name.includes("_id")) {
    return "0";
  }
  return "0";
}

function positionToLineColumn(
  text: string,
  position: number,
): { line: number; column: number } {
  const before = text.slice(0, Math.max(0, position));
  const lines = before.split("\n");
  return { line: lines.length, column: (lines.at(-1)?.length ?? 0) + 1 };
}

function simplifyJsonParseError(message: string): string {
  return message
    .replace(/^JSON\.parse: /, "")
    .replace(/ in JSON at position \d+.*$/, "")
    .trim();
}

export function validateBodyTemplateJson(template: string): {
  valid: boolean;
  error?: string;
  line?: number;
  column?: number;
} {
  const text = displayBodyTemplate(template);
  if (!text.trim()) return { valid: true };

  const stubbed = text.replace(
    PLACEHOLDER_RE,
    (_, varName: string, filter?: string) =>
      placeholderValidationStub(varName, filter),
  );

  try {
    JSON.parse(stubbed);
    return { valid: true };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Невалидный JSON";
    const positionMatch = message.match(/position (\d+)/);
    if (positionMatch) {
      const position = Number(positionMatch[1]);
      let { line, column } = positionToLineColumn(stubbed, position);
      const errorLine = stubbed.split("\n")[line - 1]?.trim() ?? "";
      const previousLine = stubbed.split("\n")[line - 2]?.trim() ?? "";
      if (line > 1 && errorLine.startsWith("}") && previousLine.endsWith(",")) {
        line -= 1;
        column = previousLine.length + 1;
      }
      return {
        valid: false,
        error: simplifyJsonParseError(message),
        line,
        column,
      };
    }
    return { valid: false, error: simplifyJsonParseError(message) };
  }
}

export const BODY_TEMPLATE_SYSTEM_SOURCES = [
  { id: "@formId", label: "ID формы" },
  { id: "@formName", label: "Название" },
  { id: "@submissionId", label: "ID ответа" },
  { id: "@submissionUuid", label: "UUID ответа" },
  { id: "@submittedAt", label: "Дата ответа" },
  { id: "@deliveryId", label: "Delivery ID" },
  { id: "@answers", label: "Ответы на все вопросы" },
] as const;
